import type { ReactElement } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Eye, Info, Power, Shield } from 'lucide-react';
import type {
  PolicyModeType,
  EnforceDisabledReason,
} from '@kirin/api-client/contracts/policies';
import {
  ENFORCE_DISABLED_REASONS,
  PolicyMode,
} from '@kirin/api-client/contracts/policies';
import { MODE_LABELS } from '../constants';

interface PolicyModeSelectorProps {
  value: PolicyModeType;
  onChange: (mode: PolicyModeType) => void;
  disabled?: boolean;
  supportsEnforce?: boolean;
  enforceDisabledReason?: EnforceDisabledReason;
  enforceTooltip?: string;
  /**
   * Visual variant. `contrast` renders the tabs list with a darker background
   * so the Off/Monitor/Enforce selector reads more clearly inside dialog
   * surfaces (KIR-3550 mockup). Defaults to `default` (bg-muted).
   */
  variant?: 'default' | 'contrast';
  /**
   * `sm` shrinks the control and drops the full-width stretch so it can sit
   * beside a label rather than under it (KIR-4426's nested sub-policy card).
   * Defaults to `default`, which every existing call site relies on.
   */
  size?: 'default' | 'sm';
}

const MODE_ORDER: PolicyModeType[] = [
  PolicyMode.Off,
  PolicyMode.Monitor,
  PolicyMode.Enforce,
];

function getModeIcon(mode: PolicyModeType): ReactElement {
  switch (mode) {
    case PolicyMode.Off:
      return <Power className="size-3.5" aria-hidden />;
    case PolicyMode.Monitor:
      return <Eye className="size-3.5" aria-hidden />;
    case PolicyMode.Enforce:
      return <Shield className="size-3.5" aria-hidden />;
    default:
      return <Power className="size-3.5" aria-hidden />;
  }
}

export function PolicyModeSelector({
  value,
  onChange,
  disabled = false,
  supportsEnforce = true,
  enforceDisabledReason,
  enforceTooltip,
  variant = 'default',
  size = 'default',
}: PolicyModeSelectorProps): ReactElement {
  const isSmall = size === 'sm';
  const handleValueChange = (v: string): void => {
    const mode = v as PolicyModeType;
    if (mode === PolicyMode.Enforce && !supportsEnforce) return;
    onChange(mode);
  };

  // Enforce disabled because the feature flag is off → direct users to support.
  // Otherwise (disabled by design) keep the "Coming Soon" copy.
  const enforceDisabledTooltip =
    enforceDisabledReason === ENFORCE_DISABLED_REASONS.FeatureFlag
      ? 'Contact Support to enable Enforce'
      : 'Coming Soon';

  return (
    <Tabs
      value={value}
      onValueChange={handleValueChange}
      className={isSmall ? 'w-auto' : 'w-full'}
    >
      <TabsList
        className={cn(
          'grid grid-cols-3',
          isSmall ? 'inline-grid w-auto h-7' : 'w-full h-8',
          variant === 'contrast' && 'bg-muted-foreground/10',
          disabled && 'pointer-events-none opacity-60'
        )}
      >
        {MODE_ORDER.map(mode => {
          const isEnforce = mode === PolicyMode.Enforce;
          const isEnforceDisabled = isEnforce && !supportsEnforce;
          // When "Coming Soon", leave trigger enabled so it receives hover and shows tooltip; handleValueChange blocks the switch.
          // Use aria-disabled and tabIndex to prevent keyboard/screen-reader selection while keeping hover for tooltip.
          const trigger = (
            <TabsTrigger
              key={mode}
              value={mode}
              disabled={disabled}
              aria-disabled={isEnforceDisabled}
              tabIndex={isEnforceDisabled ? -1 : undefined}
              className={cn(
                'text-xs',
                isSmall && 'px-2 py-0.5 text-[11px]',
                'data-[state=active]:text-accent-foreground data-[state=active]:border-accent-border',
                isEnforceDisabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <span className="inline-flex items-center gap-1.5">
                {getModeIcon(mode)}
                <span>{MODE_LABELS[mode]}</span>
                {isEnforce && (
                  <span className="inline-flex size-3.5 items-center justify-center">
                    {!isEnforceDisabled && enforceTooltip ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            aria-label="Enforce info"
                            className="inline-flex items-center justify-center rounded-full border border-current/40 p-[1px] opacity-90"
                          >
                            <Info className="size-2.5" aria-hidden />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{enforceTooltip}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="size-3.5" aria-hidden />
                    )}
                  </span>
                )}
              </span>
            </TabsTrigger>
          );
          if (isEnforceDisabled) {
            return (
              <Tooltip key={mode}>
                <TooltipTrigger asChild>{trigger}</TooltipTrigger>
                <TooltipContent>{enforceDisabledTooltip}</TooltipContent>
              </Tooltip>
            );
          }
          return trigger;
        })}
      </TabsList>
    </Tabs>
  );
}

PolicyModeSelector.displayName = 'PolicyModeSelector';
